import { DateTime } from 'luxon';
import { action, makeAutoObservable } from 'mobx';
import { LoanApiResponse, LoansApi, LoanTransactionDetailsApiResponse, OrgLoanApiResponse } from '../../api/Loans';
import { LoanErrorType } from '../../config/Constants';
import { convertCurrency } from '../../utils/Numbers';
import { Entity } from './Entity';
import { DailyExchangeRates, ExchangeRates } from './ExchangeRates';
import { Group } from './Group';
import { LoanTransactionDetails } from './LoanTransactionDetails';
import { InterestRates } from './InterestRates';
import { CurrencyCode } from '../../config/CurrencyCode';
import { Posting } from './Posting';
import { Autocompletable } from '../../components/form/Autocomplete';

export class OrgLoanSummary {
  balance: number = 0;
  accountCode: string = null;
  monthInterestToDate: number = 0;
  lastFxPosting: Posting = null;
  lastInterestPosting: Posting = null;
  cashInTransit: number = 0;

  static fromApi(apiData: OrgLoanApiResponse): OrgLoanSummary {
    const summary = new OrgLoanSummary();

    summary.balance = apiData.balance;
    summary.accountCode = apiData.accountCode;
    summary.monthInterestToDate = apiData.monthInterestToDate;
    summary.lastFxPosting = apiData.latestFxPosting ? Posting.fromApi(apiData.latestFxPosting) : null;
    summary.lastInterestPosting = apiData.latestInterestPosting ? Posting.fromApi(apiData.latestInterestPosting) : null;
    summary.cashInTransit = apiData.cashInTransit;

    return summary;
  }

  hasInterestPostingForMonth = (month: DateTime): boolean => {
    return this.lastInterestPosting !== null && this.lastInterestPosting.issueDate > month.startOf('month');
  };

  hasFxPostingForMonth = (month: DateTime) => {
    return this.lastFxPosting !== null && this.lastFxPosting.issueDate >= month.startOf('month');
  };
}

export class LoanSummary {
  loan: Loan = null;
  org1: OrgLoanSummary = null;
  org2: OrgLoanSummary = null;
  exchangeRate: DailyExchangeRates = null;
  interestRates: InterestRates = null;
  date: DateTime = null;

  constructor() {
    makeAutoObservable(this);
  }

  static fromApi(loan: Loan, date: DateTime, apiData: LoanApiResponse): LoanSummary {
    const summary = new LoanSummary();

    summary.loan = loan;
    summary.date = date;
    summary.org1 = OrgLoanSummary.fromApi(apiData.org1);
    summary.org2 = OrgLoanSummary.fromApi(apiData.org2);
    summary.exchangeRate = DailyExchangeRates.fromApi(apiData.exchangeRates);
    summary.interestRates = InterestRates.fromApi(apiData.interestRates);

    return summary;
  }

  get balance(): number {
    return this.org1.balance;
  }

  get borrowerBalance(): number {
    return this.org2.balance;
  }

  get lenderAccountCode(): string {
    return this.org1.accountCode;
  }

  get borrowerAccountCode(): string {
    return this.org2.accountCode;
  }

  get lenderInterest(): number {
    return this.org1.monthInterestToDate;
  }

  get borrowerInterest(): number {
    return -convertCurrency(
      this.lenderInterest,
      this.loan.lender.currency,
      this.loan.borrower.currency,
      this.exchangeRate
    );
  }

  /**
   * Error is calculated by this formula:
   *  lenderBalance + borrowerBalance + error = 0
   * lenderBalance & borrowerBalance should be opposite signs
   */
  get error(): number {
    return parseFloat((
      -this.balance -
      convertCurrency(
        this.borrowerBalance,
        this.loan.borrower.currency,
        this.loan.lender.currency,
        this.exchangeRate
      )
    ).toFixed(2));
  }

  get errorBorrowerCurrency(): number {
    return convertCurrency(
      this.error,
      this.loan.lender.currency,
      this.loan.borrower.currency,
      this.exchangeRate
    );
  }

  get errorType(): LoanErrorType {
    if (Math.abs(this.error) < 0.005) {
      return LoanErrorType.NONE;
    }

    if (this.loan.lender.currency != this.loan.borrower.currency) {
      return LoanErrorType.FX;
    }

    return LoanErrorType.TX_MISSING;
  }

  get lender(): Entity {
    return this.loan.lender;
  }

  get borrower(): Entity {
    return this.loan.borrower;
  }

  get currency(): CurrencyCode {
    return this.lender.currency;
  }

  get isCrossCurrency(): boolean {
    return this.lender.currency != this.borrower.currency;
  }

  involves = (e: Entity): boolean => {
    return this.lender.id === e.id || this.borrower.id === e.id;
  };

  balanceIn = (e: Entity): number => {
    if (!this.involves(e)) return 0;
    return this.lender.id === e.id ? this.balance : this.borrowerBalance;
  };

  balanceAgainst = (e: Entity): number => {
    if (!this.involves(e)) return 0;
    const isLender = this.lender.id === e.id;
    return convertCurrency(
      isLender ? this.borrowerBalance : this.balance,
      isLender ? this.borrower.currency : this.lender.currency,
      e.currency,
      this.exchangeRate
    );
  };

  get isOutsideDiscrepancyThreshold(): boolean {
    const discrepancyAllowance = this.loan.group.balancerSettings.balanceDiscrepancy;
    return Math.abs(this.error) > (discrepancyAllowance === 0 ? 0.01 : discrepancyAllowance);
  }
}

export class Loan {
  id: number = 0;
  group: Group = null;
  org1: Entity = null;
  org1Info: OrgLoanSummary = null;
  org2: Entity = null;
  org2Info: OrgLoanSummary = null;
  exchangeRates: ExchangeRates = null;
  loanLender: number = 0;
  
  summaries: Map<string, LoanSummary> = new Map();

  transactions: LoanTransactionDetails[] = [];
  overriddenTransactions: LoanTransactionDetails[] = [];
  hasLoadedTransactions: boolean = false;
  accountId: number = 0;
  org1CashInTransitEOM: Map<string, number> = new Map();
  org2CashInTransitEOM: Map<string, number> = new Map();

  _selectedLenderCurrency: CurrencyCode = null;
  _selectedBorrowerCurrency: CurrencyCode = null;

  constructor() {
    makeAutoObservable(this);
  }

  static fromApi(group: Group, apiData: LoanApiResponse, date: DateTime): Loan {
    const loan = new Loan();

    loan.group = group;

    loan.id = apiData.id;

    loan.parseApi(apiData, date);

    return loan;
  }

  parseApi = (apiData: LoanApiResponse, date: DateTime) => {
    this.summaries.set(date.toISODate(), LoanSummary.fromApi(this, date, apiData));
    this.accountId = apiData.account.id;
    this.loanLender = apiData.account.org1.isLender ? apiData.account.org1.id : (apiData.account.org2.isLender ? apiData.account.org2.id : 0);
    this.org1 = this.group.getEntity(apiData.org1.id);
    this.org2 = this.group.getEntity(apiData.org2.id);
    this.org1Info = OrgLoanSummary.fromApi(apiData.org1);
    this.org2Info = OrgLoanSummary.fromApi(apiData.org2);
  };

  get lender(): Entity {
    return this.loanLender == this.org2.id ? this.org2 : this.org1;
  }

  get borrower(): Entity {
    return this.loanLender == this.org2.id ? this.org1 : this.org2;
  }

  get lenderInfo(): OrgLoanSummary {
    return this.loanLender == this.org2.id ? this.org2Info : this.org1Info;
  }

  get borrowerInfo(): OrgLoanSummary {
    return this.loanLender == this.org2.id ? this.org1Info : this.org2Info;
  }

  get latestSummary(): LoanSummary {
    let latest: LoanSummary = null;

    [...this.summaries.values()].forEach(summary => {
      if (!latest || summary.date > latest.date) {
        latest = summary;
      }
    });

    return latest;
  }

  hasSummary = (date: DateTime): boolean => this.summaries.has(date.toISODate());

  getSummary = (date: DateTime): LoanSummary => this.summaries.get(date.toISODate());

  loadTransactions = (force: boolean = false): Promise<LoanTransactionDetails[]> => {
    if (this.hasLoadedTransactions && !force) {
      return Promise.resolve(this.transactions);
    }

    return LoansApi.getLoanTransactions(this.group.id, this.id)
      .then(this.parseTransactionsResponse);
  };

  parseTransactionsResponse = (details: LoanTransactionDetailsApiResponse) => {
    this.exchangeRates = ExchangeRates.fromApi(details.exchangeRates);
    this.transactions = details
      .matches
      .map(transaction => LoanTransactionDetails.fromApi(details, transaction, this));

    this.transactions.sort((a, b) => a.date < b.date ? 1 : a.date > b.date ? -1 : 0);
    this.hasLoadedTransactions = true;

    this.org1CashInTransitEOM = new Map(Object.entries(details.org1cashInTransitEOM));
    this.org2CashInTransitEOM = new Map(Object.entries(details.org2cashInTransitEOM));

    this.clearOverrides();
    this.calculateVariances();

    return this.transactions;
  };

  /**
   * Clears the transactions we've loaded from the API, to save memory
   */
  unload = () => {
    this.transactions = [];
    this.hasLoadedTransactions = false;
  };

  calculateVariances = action(() => {
    for (let i = 0; i < this.transactions.length; i++) {
      if (i + 1 < this.transactions.length) {
        this.transactions[i].variance = this.transactions[i].error - this.transactions[i + 1].error;
      }
    }
  });

  org1Transactions = () => {
    return this.transactions.flatMap(t => t.lenderTransactions);
  };

  org2Transactions = () => {
    return this.transactions.flatMap(t => t.borrowerTransactions);
  };

  setOverride = (override: LoanTransactionDetails) => {
    this.overriddenTransactions.push(override);

    // Make the change to regular transactions in place
    // Backend will do this, but the quick feedback for the user will be valuable
    const txIds = override.lenderTransactions.map(t => t.id).concat(override.borrowerTransactions.map(t => t.id));

    this.transactions.forEach(lt => {
      lt.org1.transactions = lt.org1.transactions.filter(tx => !txIds.includes(tx.id));
      lt.org2.transactions = lt.org2.transactions.filter(tx => !txIds.includes(tx.id));
    });

    this.transactions = this.transactions.filter(lt => lt.org1.transactions.length > 0 || lt.org2.transactions.length > 0);
    this.transactions.push(override);
  };

  clearOverrides = () => {
    this.overriddenTransactions = [];
  };

  submitOverrides = async () => {
    const response = await LoansApi.setLoanOverrides(this.group.id, this.id, this.org1.id, this.org2.id, this.overriddenTransactions.map(t => t.toApi()));
    this.overriddenTransactions = [];

    this.parseTransactionsResponse(response);
  };

  currencyOptions = (): Autocompletable[] => {
    const currencyOptionsSet = new Set<string>();
    currencyOptionsSet.add(this.lender.currency);
    currencyOptionsSet.add(this.borrower.currency);
    const requiredCurrency = this.group.balancerSettings.requiredCurrencies?.[0];
    if (requiredCurrency != null) {
      currencyOptionsSet.add(requiredCurrency);
    }
    return Array.from(currencyOptionsSet.values()).map(v => ({ id: v, name: v }));
  };

  get selectedLenderCurrency(): CurrencyCode {
    return this._selectedLenderCurrency || this.lender.currency;
  }

  set selectedLenderCurrency(v: CurrencyCode) {
    this._selectedLenderCurrency = v;
  }

  get selectedBorrowerCurrency(): CurrencyCode {
    return this._selectedBorrowerCurrency || this.lender.currency;
  }

  set selectedBorrowerCurrency(v: CurrencyCode) {
    this._selectedBorrowerCurrency = v;
  }
}
